/**
 * The extra data a few TLDs require of the registrant (ADR-023) in forms: the starting values,
 * what is still missing, what to send, and the per-field messages of a `REGISTRANT_INCOMPLETE`
 * refusal (fields `extra.<key>`, completed on the domain or the order, never on the subject).
 * Pure functions: `npm test` covers them.
 */

import type { RegistrantProblem } from '../api/types';

/** What a form needs of a field (`GET /tlds/{zone}/extra-fields`). */
export interface ExtraFormField {
  key: string;
  sensitive: boolean;
  required_now?: boolean | null;
  prefill?: { value: string | null; hint: string | null; source: string } | null;
}

/** The `extra.<key>` prefix of refusal fields. */
export const EXTRA_PREFIX = 'extra.';

/** Whether a refusal line is about the domain's extra TLD data (not the subject). */
export function isExtraProblem(p: Pick<RegistrantProblem, 'field' | 'contact'>): boolean {
  return p.contact === 'DOMAIN' || p.field.startsWith(EXTRA_PREFIX);
}

/** The form's starting values: what fills a non-sensitive field (a sensitive one stays empty; the server fills it). */
export function initialValues(fields: readonly ExtraFormField[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of fields) {
    const v = !f.sensitive ? f.prefill?.value : null;
    if (v) out[f.key] = v;
  }
  return out;
}

/** The values to send: trimmed, empty ones left out (the server then fills what it can). */
export function valuesToSend(values: Readonly<Record<string, string>>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(values)) {
    const t = v.trim();
    if (t) out[k] = t;
  }
  return out;
}

/** Keys still to fill: required of this registrant, nothing typed, nothing that fills it. */
export function missingKeys(fields: readonly ExtraFormField[], values: Readonly<Record<string, string>>): string[] {
  return fields.filter((f) => f.required_now && !(values[f.key] ?? '').trim() && !f.prefill).map((f) => f.key);
}

/** `extra.<key>` refusal lines → key → message. */
export function extraFieldErrors(problems: readonly RegistrantProblem[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const p of problems) {
    if (p.field.startsWith(EXTRA_PREFIX)) out[p.field.slice(EXTRA_PREFIX.length)] = p.message;
  }
  return out;
}

/** The changed values of an edit: a key whose text differs from the stored value; an empty text on a set field clears it. */
export function changedValues(
  stored: readonly { key: string; value: string | null; is_set: boolean; sensitive: boolean }[],
  values: Readonly<Record<string, string>>,
): Record<string, string | null> {
  const out: Record<string, string | null> = {};
  for (const f of stored) {
    if (!(f.key in values)) continue;
    const typed = (values[f.key] ?? '').trim();
    // A sensitive value is never shown: an empty box leaves it as it is.
    if (f.sensitive && !typed) continue;
    if (!typed) {
      if (f.is_set) out[f.key] = null;
    } else if (typed !== (f.value ?? '')) {
      out[f.key] = typed;
    }
  }
  return out;
}
